import React, { useMemo, useState } from "react";
import { FiDownload, FiUploadCloud } from "react-icons/fi";
import { showErrorToast, showSuccessToast } from "../../constants/ToastService";
import { qualityApi } from "./qualityApi";
import { Modal, SHEET_COLUMNS } from "./qualityUi";
import "./Quality.css";

/**
 * Import test cases from the QA team's sheet (.xlsx or .csv).
 *
 * Choosing a file runs a dry run straight away and shows every row as OK,
 * warning, error or duplicate, so nothing is written until the preview looks
 * right. Rows with errors and rows already in the library are skipped; the
 * rest go in together. Modules / sub-modules missing from Masters can be
 * created as part of the import.
 *
 * Opened from a sprint (sprintId), the sheet also has a Ticket column and
 * each case is linked to its ticket. A case already in the library (named
 * by Test Case ID, or the same module / sub module / description) is linked
 * rather than skipped, and one case can be linked to several tickets.
 *
 * Opened from a ticket's Test Cases tab (ticketId), every row is linked to
 * that ticket and no Ticket column is needed.
 */
const STATUS = {
    ok: ["OK", "qa-pill--success"],
    warning: ["Warning", "qa-pill--warn"],
    error: ["Error", "qa-pill--danger"],
    duplicate: ["Duplicate", ""],
    link: ["Link", "qa-pill--info"],
};

export default function TestCaseImportModal({ onClose, onImported, sprintId = null, sprintName = "", sprintTickets = [],
    ticketId = null, ticketCode = "" }) {
    const opts = sprintId ? { sprintId, sprintTickets } : ticketId ? { ticketId, ticketCode } : {};
    const linking = Boolean(sprintId || ticketId);
    const [file, setFile] = useState(null);
    const [createMissing, setCreateMissing] = useState(false);
    const [preview, setPreview] = useState(null);
    const [fileError, setFileError] = useState("");
    const [busy, setBusy] = useState(false);
    const [onlyProblems, setOnlyProblems] = useState(false);

    const runPreview = async (f, create) => {
        setBusy(true);
        setFileError("");
        try {
            const res = await qualityApi.importTestCases(f, { ...opts, dryRun: true, createMissing: create });
            setPreview(res.data);
        } catch (e) {
            setPreview(null);
            setFileError(e.message || "That file could not be read.");
        } finally {
            setBusy(false);
        }
    };

    const choose = (f) => {
        if (!f) return;
        setFile(f);
        setOnlyProblems(false);
        runPreview(f, createMissing);
    };

    const toggleCreate = (on) => {
        setCreateMissing(on);
        if (file) runPreview(file, on);
    };

    const doImport = async () => {
        setBusy(true);
        try {
            const res = await qualityApi.importTestCases(file, { ...opts, dryRun: false, createMissing });
            showSuccessToast(res.message || "Test cases imported.");
            onImported && onImported(res.data);
        } catch (e) {
            showErrorToast(e.message || "Import failed.");
            setBusy(false);
        }
    };

    const template = async () => {
        try {
            const blob = await qualityApi.downloadTemplate(sprintId, ticketId);
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const scope = sprintId ? sprintName || "sprint" : ticketId ? ticketCode || "ticket" : "";
            const base = scope ? `test_cases_${scope.replace(/\W+/g, "_")}` : "test_case_import_template";
            a.download = `${base}.${blob.type.includes("csv") ? "csv" : "xlsx"}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) {
            showErrorToast(e.message || "Could not download the template.");
        }
    };

    const s = preview?.summary;
    const rows = useMemo(
        () => (preview?.rows || []).filter((r) => !onlyProblems || r.status !== "ok"),
        [preview, onlyProblems]
    );

    return (
        <Modal
            wide
            title={sprintId ? `Import & link test cases · ${sprintName || "sprint"}`
                : ticketId ? `Import & link test cases · ${ticketCode || "ticket"}` : "Import test cases"}
            onClose={onClose}
            footer={
                <>
                    <span className="qa-hint">
                        {s ? (ticketId
                            ? `${s.new_cases} new case${s.new_cases === 1 ? "" : "s"}, ${s.ready} to link to ${ticketCode || "this ticket"}.`
                            : sprintId
                            ? `${s.new_cases} new case${s.new_cases === 1 ? "" : "s"}, ${s.ready} link${s.ready === 1 ? "" : "s"} to ${s.tickets} ticket${s.tickets === 1 ? "" : "s"}.`
                            : `${s.ready} of ${s.total} row${s.total === 1 ? "" : "s"} will be imported.`)
                            : "Nothing is saved until you import."}
                    </span>
                    <span className="qa-sp" />
                    <button type="button" className="qa-btn" onClick={onClose} disabled={busy}>Cancel</button>
                    <button type="button" className="qa-btn qa-btn--primary" onClick={doImport} disabled={busy || !s || !s.ready}>
                        {busy && s ? "Working…" : linking ? `Import & link ${s ? s.ready : ""}`
                            : `Import ${s ? s.ready : ""} test case${s && s.ready === 1 ? "" : "s"}`}
                    </button>
                </>
            }
        >
            <div className="qa-import-top">
                <label className="qa-drop">
                    <input id="qa-import-file" type="file" accept=".xlsx,.xlsm,.csv"
                        onChange={(e) => { choose(e.target.files[0]); e.target.value = ""; }} />
                    <FiUploadCloud size={14} /> {file ? `Change file (${file.name})` : "Choose .xlsx or .csv file"}
                </label>
                <button type="button" className="qa-linkbtn" onClick={template}>
                    <FiDownload size={12} /> Download template
                </button>
            </div>
            <p className="qa-hint" style={{ margin: "10px 0 0" }}>
                Columns: {sprintId && <><b>Ticket</b> · Test Case ID (optional) · </>}{ticketId && <>Test Case ID (optional) · </>}{SHEET_COLUMNS.join(" · ")}. The first row
                must be the header. Put each test step on its own line; numbering like "1." is removed.
            </p>
            {sprintId && (
                <p className="qa-hint" style={{ margin: "6px 0 0" }}>
                    Ticket is a code from this sprint, e.g. {sprintTickets[0]?.code || "S 52-01"}. To link a case that is already
                    in the library, put its code (TC-0013) in Test Case ID and leave the other columns empty. The template lists
                    this sprint's tickets on a second sheet.
                </p>
            )}
            {ticketId && (
                <p className="qa-hint" style={{ margin: "6px 0 0" }}>
                    Every row is linked to {ticketCode || "this ticket"}. To link a case already in the library, put its code
                    (TC-0013) in Test Case ID and leave the other columns empty.
                </p>
            )}

            {fileError && (
                <div className="qa-banner qa-banner--bad" style={{ marginTop: 14 }}>
                    <span className="qa-banner__tx">{fileError}</span>
                </div>
            )}
            {busy && !preview && <div className="qa-empty">Reading the file…</div>}

            {s && (
                <>
                    <div className="qa-import-summary">
                        <span className="qa-chip">{s.total} rows</span>
                        <span className="qa-chip qa-chip--green">{s.ready} ready</span>
                        {s.with_warnings > 0 && <span className="qa-chip qa-chip--amber">{s.with_warnings} with warnings</span>}
                        {s.errors > 0 && <span className="qa-chip qa-chip--red">{s.errors} with errors (skipped)</span>}
                        {linking && <span className="qa-chip qa-chip--green">{s.new_cases} new</span>}
                        {s.to_link > 0 && <span className="qa-chip qa-chip--blue">{s.to_link} existing to link</span>}
                        {sprintId && <span className="qa-chip">{s.tickets} ticket{s.tickets === 1 ? "" : "s"}</span>}
                        {s.duplicates > 0 && <span className="qa-chip">{s.duplicates} duplicates (skipped)</span>}
                        <span className="qa-sp" />
                        <label className="qa-check" style={{ fontSize: ".72rem" }}>
                            <input type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} />
                            <span>Show only rows that need attention</span>
                        </label>
                    </div>

                    {s.unknown_columns?.length > 0 && (
                        <p className="qa-hint" style={{ margin: "0 0 10px" }}>
                            Ignored columns: {s.unknown_columns.join(", ")}.
                        </p>
                    )}
                    {(s.missing_modules?.length > 0 || s.missing_sub_modules?.length > 0 || createMissing) && (
                        <div className="qa-banner qa-banner--warn">
                            <span className="qa-banner__tx">
                                {s.missing_modules?.length > 0 && <div>Modules not in Masters: <b>{s.missing_modules.join(", ")}</b></div>}
                                {s.missing_sub_modules?.length > 0 && <div>Sub modules not in Masters: <b>{s.missing_sub_modules.join(", ")}</b></div>}
                                <div>
                                    Rows using these are errors unless you create them
                                    {createMissing ? " (they will be created when you import)." : ", or add them in Masters first."}
                                </div>
                            </span>
                            <label className="qa-check" style={{ fontSize: ".74rem" }}>
                                <input id="qa-import-create" type="checkbox" checked={createMissing} disabled={busy}
                                    onChange={(e) => toggleCreate(e.target.checked)} />
                                <span>Create missing modules and sub modules</span>
                            </label>
                        </div>
                    )}

                    <div className="qa-card qa-card--solo qa-import-table">
                        <table className="qa-tbl">
                            <thead>
                                <tr>
                                    <th>Row</th><th>S.No</th>{sprintId && <th>Ticket</th>}<th>Status</th><th>Notes</th><th>Module</th><th>Sub Module</th>
                                    <th>Scenario / Description</th><th>Steps</th><th>Type</th><th>Severity</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 && <tr><td colSpan={sprintId ? 11 : 10} className="qa-empty">Every row is ready.</td></tr>}
                                {rows.map((r) => {
                                    const [label, cls] = STATUS[r.status] || [r.status, ""];
                                    return (
                                        <tr key={r.line}>
                                            <td className="qa-num qa-muted">{r.line}</td>
                                            <td className="qa-num">{r.sno || "—"}</td>
                                            {sprintId && <td className="qa-nw qa-mono">{r.ticket || <span className="qa-faint">—</span>}</td>}
                                            <td><span className={`qa-pill ${cls}`}>{label}</span></td>
                                            <td className="qa-wrap" style={{ color: r.status === "error" ? "var(--qa-red)" : undefined, fontSize: ".74rem" }}>
                                                {r.messages.length ? r.messages.map((m) => <div key={m}>{m}</div>) : <span className="qa-faint">—</span>}
                                            </td>
                                            <td className="qa-nw">{r.module || <span className="qa-faint">—</span>}</td>
                                            <td className="qa-nw">{r.sub_module || <span className="qa-faint">—</span>}</td>
                                            <td>
                                                {r.scenario && <div style={{ fontWeight: 500 }}>{r.scenario}</div>}
                                                <div className="qa-trunc" style={{ maxWidth: 320 }}>{r.description}</div>
                                            </td>
                                            <td className="qa-num">{r.steps}</td>
                                            <td className="qa-nw">{r.case_type}</td>
                                            <td className="qa-nw">{r.severity || "—"}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </Modal>
    );
}
